/**
 * 도움말 popover — 헤더 도움말 아이콘 트리거.
 *
 * Figma: Design System 0.1 > IXD-DSD Reconciliation > Info Modal
 *
 * 4개 탭으로 구성:
 *  - 비교 결과: 5가지 상태(기본/초록/노랑/빨강/회색) 의미 설명
 *  - 합계 검증: 합계 검증 floating panel 의 구성요소 안내 + 미니 미리보기
 *  - 라인아이템: 라인아이템 연동 안내 + 미니 미리보기
 *  - 위치 확인: DSD 미리보기 위 「IXD 위치 확인」 탐색 바와 점의 의미
 *
 * 트리거는 caller 가 children 으로 넘긴다 (Radix asChild 패턴).
 */

import * as Popover from "@radix-ui/react-popover";
import { X } from "lucide-react";
import type { ReactElement } from "react";
import { cn } from "@/lib/utils";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/toggle-ui-kit/tabs";
import { LineItemPreview } from "./compare-help-line-item-preview";
import { SumValidationPreview } from "./compare-help-sum-validation-preview";

const TAB_VALUES = [
  "compare-result",
  "sum-validation",
  "line-item",
  "position-check",
] as const;
type TabValue = (typeof TAB_VALUES)[number];

interface CompareHelpPopoverProps {
  /** popover trigger — Radix asChild 로 wrap. 단일 React element 필요. */
  children: ReactElement;
  /** 처음 열릴 탭. 기능 옆 「?」 가 자기 설명 탭을 바로 열 때 쓴다 */
  defaultTab?: TabValue;
}

const TAB_LABELS: Record<TabValue, string> = {
  "compare-result": "비교 결과",
  "sum-validation": "합계 검증",
  "line-item": "라인아이템",
  "position-check": "위치 확인",
};

// Figma Shadow/XLarge 정확 매핑
const SHADOW_XLARGE =
  "shadow-[0_4px_24px_4px_rgba(0,0,0,0.08),0_20px_25px_-5px_rgba(0,0,0,0.1)]";

export const CompareHelpPopover = ({
  children,
  defaultTab = "compare-result",
}: CompareHelpPopoverProps) => {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          collisionPadding={16}
          className={cn(
            "z-50 w-[460px] border border-line-normal overflow-hidden rounded-[8px] bg-background-base",
            SHADOW_XLARGE,
          )}
        >
          <Tabs defaultValue={defaultTab}>
            {/* Header — 탭 리스트 + 닫기 X.
                TabsList 의 기본 하단 보더는 wrapping div 가 담당하므로 비활성화. */}
            <div className="flex items-center justify-between border-b border-line-normal">
              <TabsList className="border-b-0">
                {TAB_VALUES.map((value) => (
                  <TabsTrigger key={value} value={value}>
                    {TAB_LABELS[value]}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="flex justify-center items-center px-3">
                <Popover.Close asChild>
                  <IconButton
                    variant="transparent"
                    icon={<X />}
                    aria-label="닫기"
                  />
                </Popover.Close>
              </div>
            </div>

            <TabsContent value="compare-result">
              <CompareResultTab />
            </TabsContent>
            <TabsContent value="sum-validation">
              <SumValidationTab />
            </TabsContent>
            <TabsContent value="line-item">
              <LineItemTab />
            </TabsContent>
            <TabsContent value="position-check">
              <PositionCheckTab />
            </TabsContent>
          </Tabs>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

// ============================================
// 공통: 단계 번호 배지 (원형, 진한 배경 + 흰 텍스트)
// ============================================

const StepBadge = ({ n }: { n: number }) => (
  <div className="flex size-3 text-[0.515625rem] shrink-0 items-center justify-center rounded-full bg-label-normal text-static-white">
    {n}
  </div>
);

// ============================================
// 비교 결과 탭
// ============================================

interface CompareStatusItem {
  bgClass: string;
  borderClass?: string;
  value: string;
  valueClass: string;
  name: string;
  description: string;
}

const COMPARE_RESULT_ITEMS: CompareStatusItem[] = [
  {
    bgClass: "bg-background-base",
    value: "-",
    valueClass: "text-label-normal",
    name: "기본",
    description: "대조 대상이 아닌 값입니다.",
  },
  {
    bgClass: "bg-green-200",
    value: "418,776",
    valueClass: "text-label-normal",
    name: "초록",
    description: "IXD의 값과 숫자 및 부호가 완벽히 일치합니다.",
  },
  {
    bgClass: "bg-yellow-200",
    value: "- 418,776",
    valueClass: "text-label-normal",
    name: "노랑",
    description:
      "숫자는 일치하지만 부호가 다른 값입니다.\n(negated 설정 등 확인 필요)",
  },
  {
    bgClass: "bg-rose-200",
    value: "418,776",
    valueClass: "text-label-normal",
    name: "빨강",
    description:
      "IXD에서 같은 값을 찾지 못했습니다.\n(표는 위치 불일치, 문장주석은 미태깅 금액)\n입력된 값을 다시 확인해 주세요.",
  },
  {
    bgClass: "bg-background-base",
    value: "418,776",
    valueClass: "text-label-assistive",
    name: "회색",
    description: "DSD에만 값이 존재하며,\nIXD에는 값이 누락되어 있습니다.",
  },
];

const CompareResultTab = () => (
  <div className="flex flex-col gap-2.5 p-4">
    <div className="label-2-medium text-label-normal">
      IXD와 DSD의 값을 대조하여, 일치 여부를 5가지 상태로 표시합니다.
    </div>
    <ul className="flex flex-col gap-5">
      {COMPARE_RESULT_ITEMS.map((item) => (
        <li key={item.name} className="flex items-center gap-4">
          <div
            className={cn(
              "flex h-[34px] w-[80px] shrink-0 items-center justify-end border border-line-normal p-2 label-2-medium",
              item.bgClass,
              item.valueClass,
            )}
          >
            {item.value}
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="label-2-semibold text-label-normal">
              {item.name}
            </span>
            <span className="whitespace-pre-line label-2-medium text-label-neutral">
              {item.description}
            </span>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

// ============================================
// 합계 검증 탭
// ============================================

const SUM_VALIDATION_STEPS = [
  {
    n: 1,
    title: "입력한 값",
    description: "사용자가 IXD에 직접 입력한 값입니다.",
  },
  {
    n: 2,
    title: "계산 결과",
    description: "도메인의 하위 항목을 합산하여 시스템이 산출한 값입니다.",
  },
  {
    n: 3,
    title: "연동 계정",
    description:
      "해당 항목은 다른 본문, 주석에 연동되어 있어, 값 변경이 영향이 갈 수 있으므로 값 수정 시 주의가 필요합니다.",
  },
];

const SumValidationTab = () => (
  <div className="flex gap-2.5 p-4">
    <div className="flex flex-1 flex-col gap-4">
      <p className="label-2-medium text-label-normal">
        입력된 값의 합계가 정확한지 검증하고,
        <br />
        연동된 항목을 안내합니다
      </p>
      <ol className="flex flex-col gap-3">
        {SUM_VALIDATION_STEPS.map((step) => (
          <li key={step.n} className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <StepBadge n={step.n} />
              <span className="label-2-medium text-label-normal">
                {step.title}
              </span>
            </div>
            <div className="pl-4 label-2-medium text-label-neutral">
              {step.description}
            </div>
          </li>
        ))}
      </ol>
    </div>
    <SumValidationPreview />
  </div>
);

// ============================================
// 라인아이템 탭
// ============================================

const LINE_ITEM_STEPS = [
  {
    n: 1,
    title: "현재 라인아이템",
    description:
      "현재 화면에서 확인 중인 라인아이템은 파란색으로 강조되어 표시됩니다.",
  },
  {
    n: 2,
    title: "연동 항목 이동",
    description:
      "목록에서 다른 항목을 클릭하면 해당 값이 위치한 본문 및 주석으로 즉시 이동합니다.",
  },
];

const LineItemTab = () => (
  <div className="flex gap-2.5 p-4">
    <LineItemPreview />
    <div className="flex flex-1 flex-col gap-3">
      <div className="label-2-medium text-label-normal">
        IXD 상에서 반복적으로 사용되어 연동 가능성이 높은 라인아이템에 대해
        안내합니다.
      </div>
      <ol className="flex flex-col gap-3">
        {LINE_ITEM_STEPS.map((step) => (
          <li key={step.n} className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <StepBadge n={step.n} />
              <span className="label-2-semibold text-label-normal">
                {step.title}
              </span>
            </div>
            <p className="pl-5 label-2-medium text-label-neutral">
              {step.description}
            </p>
          </li>
        ))}
      </ol>
    </div>
  </div>
);

// ============================================
// 위치 확인 탭
// ============================================

const PositionCheckTab = () => (
  <div className="flex flex-col gap-3 p-4 label-2-medium text-label-neutral">
    <p className="m-0 text-label-normal">
      값 검토는 두 단계로 합니다. ① <b>값 존재</b> — 칸 색으로 DSD 값이 IXD에
      있는지 먼저 봅니다(빨간 칸부터). ② <b>값 위치</b> — 찾은 값이 맞는 행·열에
      들어갔는지, 확인해 볼 만한 줄을 골라 차례로 보여 줍니다.
    </p>
    <ul className="m-0 flex flex-col gap-2 pl-0">
      <li className="flex items-center gap-2">
        <span className="inline-block size-2 shrink-0 rounded-full bg-slate-500" />
        머리 이름과 IXD 이름이 다른 줄
      </li>
      <li className="flex items-center gap-2">
        <span className="inline-block size-2 shrink-0 rounded-full bg-amber-400" />
        값이 IXD 여러 곳으로 갈라진 줄
      </li>
    </ul>
    <p className="m-0">
      <kbd>I</kbd> 검토 시작 · <kbd>Esc</kbd> 검토 끝내기. 검토 중에는{" "}
      <kbd>→</kbd> 다음 · <kbd>←</kbd> 이전. 줄로 이동하면 그 줄의 DSD 칸과 IXD
      칸이 함께 강조됩니다.
    </p>
    <div className="flex flex-col gap-1 rounded-[6px] bg-neutral-100 p-3">
      <span className="label-2-semibold text-label-normal">
        검토를 돕는 기능이에요 — 이것만 보면 끝나는 검증은 아닙니다
      </span>
      <span>이 목록에 들어 있지 않은 것:</span>
      <ul className="m-0 flex list-disc flex-col gap-0.5 pl-5">
        <li>
          이름이 머리와 같은 줄 — 이름이 같아도 값이 다른 칸에 들어갈 수 있어요
        </li>
        <li>IXD에서 값을 찾지 못한 칸(빨강·회색)</li>
        <li>글자 값, 문장 속 금액, 칸이 하나뿐인 표</li>
      </ul>
      <span className="text-label-alternative">
        이름이 달라도 정상일 수 있습니다(예: 「영업이익」 ↔
        「영업이익(손실)」).
      </span>
    </div>
  </div>
);
